import {AsyncPage, Empty} from 'Core/models/Async';
import {GenelcoBatchFile} from 'Enrollment/models/GenelcoBatchFile';
import {createAction} from 'Shared/utilities/actionCreator';
import {Page} from 'Core/models/Page';
import {pipe} from 'fp-ts/lib/pipeable';
import {fold as foldOption, fromNullable} from 'fp-ts/lib/Option';
import {constant} from 'fp-ts/lib/function';
import {PageFetchAction} from 'Shared/utilities/sagaApiGenerators';
import { DateRange, EmptyDateRange } from 'Core/models/DateRange';
import { InequalityRangeFilter } from 'Core/models/InequalityRange';
import * as O from 'fp-ts/lib/Option';
import { Sort } from 'Shared/components/PagedView/Table/Sort';


export enum GenelcoBatchFileActionType {
  GET_GENELCO_BATCH_FILES = 'enrollmentFile/GET_GENELCO_BATCH_FILES',
  CHANGE_GENELCO_BATCH_FILES = 'enrollmentFile/CHANGE_GENELCO_BATCH_FILES',
  CHANGE_CHANGE_GENELCO_BATCH_FILES_FILTER = 'enrollmentFile/CHANGE_CHANGE_GENELCO_BATCH_FILES_FILTER',
  REGENERATE_BATCH_FILE = 'enrollmentFile/REGENERATE_BATCH_FILE',
  CHANGE_REGENERATE_BATCH_FILE = 'enrollmentFile/CHANGE_REGENERATE_BATCH_FILE',
  REAUDIT_BATCH_FILE = 'enrollmentFile/REAUDIT_BATCH_FILE',
  CHANGE_REAUDIT_BATCH_FILE = 'enrollmentFile/CHANGE_REAUDIT_BATCH_FILE',
  CLEAR_GENELCO_BATCH_FILES = 'enrollmentFile/CLEAR_GENELCO_BATCH_FILES',
  CHANGE_GENELCO_BATCH_FILE_SORT = 'enrollmentFile/CHANGE_GENELCO_BATCH_FILE_SORT',
  CLEAR_GENELCO_BATCH_FILE_SORT = 'enrollmentFile/CLEAR_GENELCO_BATCH_FILE_SORT'
}

export const EmptyGenelcoBatchFileFilters: GenelcoBatchFileFilters = {
  batchFileCreationDate : EmptyDateRange,
  lastBatch : EmptyDateRange,
  lastAudit: EmptyDateRange,
  applicationCount:  O.none,
 failedApplicationCount: O.none,
 processingCompletedDate: EmptyDateRange,
  destinationFolder: ''
};

export type GenelcoBatchFileFilters = {
  batchFileCreationDate : DateRange;
  lastBatch : DateRange;
  lastAudit: DateRange;
  applicationCount: InequalityRangeFilter;
  failedApplicationCount: InequalityRangeFilter;
 processingCompletedDate: DateRange;
  destinationFolder: string

};

export const getGenelcoBatchFile = (pageOption?: Page<GenelcoBatchFile>, filter?: GenelcoBatchFileFilters, sort?: Sort) => {
  const payload = pipe(
    O.fromNullable(pageOption),
    O.fold(constant(PageFetchAction.Initial({filter})), page =>
      PageFetchAction.Additional({existingPage: page, filter})
    )
  );
  return createAction(GenelcoBatchFileActionType.GET_GENELCO_BATCH_FILES, payload, sort!);
};

export const changeGenelcoBatchFile = (genelcoBatchFiles: AsyncPage<Page<GenelcoBatchFile>>) =>
  createAction(GenelcoBatchFileActionType.CHANGE_GENELCO_BATCH_FILES, genelcoBatchFiles);

export const changeGenelcoBatchFileFilters = (filters: GenelcoBatchFileFilters) =>
  createAction(GenelcoBatchFileActionType.CHANGE_CHANGE_GENELCO_BATCH_FILES_FILTER, {filters});

export const reGenerateBatchFile = (filters: GenelcoBatchFileFilters, processingCompletedDate?: Date) =>
  createAction(GenelcoBatchFileActionType.REGENERATE_BATCH_FILE, {processingCompletedDate, filters});

export const changeReGenerateBatchFile = (isRegenerated: Date[]) =>
  createAction(GenelcoBatchFileActionType.CHANGE_REGENERATE_BATCH_FILE, {isRegenerated});  

export const reAuditBatchFile = (filters: GenelcoBatchFileFilters, processingCompletedDate?: Date) =>
  createAction(GenelcoBatchFileActionType.REAUDIT_BATCH_FILE, {processingCompletedDate, filters});

export const changeReAuditBatchFile = (isAuditCompleted: Date[]) =>
  createAction(GenelcoBatchFileActionType.CHANGE_REAUDIT_BATCH_FILE, {isAuditCompleted});

export const clearGenelcoBatchFiles = () =>
 createAction(GenelcoBatchFileActionType.CLEAR_GENELCO_BATCH_FILES);

export const changeGenelcoBatchFileSort = (sort?: Sort) => createAction(GenelcoBatchFileActionType.CHANGE_GENELCO_BATCH_FILE_SORT, {sort});

export const clearGenelcoBatchFileSort = () => createAction(GenelcoBatchFileActionType.CLEAR_GENELCO_BATCH_FILE_SORT);

type GenelcoBatchFileManagementState = {
  genelcoBatchFileManagementPage: AsyncPage<Page<GenelcoBatchFile>>;
  genelcoBatchFileFilters: GenelcoBatchFileFilters;
  isBatchRegenerationCompleted: Date[];
  isBatchReAuditCompleted: Date[];
  sort: O.Option<Sort>;
};

export const DEFAULT_STATE: GenelcoBatchFileManagementState = {
  genelcoBatchFileManagementPage: new Empty(),
  genelcoBatchFileFilters: EmptyGenelcoBatchFileFilters,
  isBatchRegenerationCompleted: [],
  isBatchReAuditCompleted: [],
  sort: O.none,
};

type GenelcoBatchFileAction =
  | ReturnType<typeof getGenelcoBatchFile>
  | ReturnType<typeof changeGenelcoBatchFile>
  | ReturnType<typeof changeGenelcoBatchFileFilters>
  | ReturnType<typeof changeReGenerateBatchFile>
  | ReturnType<typeof changeReAuditBatchFile>
  | ReturnType<typeof clearGenelcoBatchFiles>
  | ReturnType<typeof changeGenelcoBatchFileSort>;

export default (
  state: GenelcoBatchFileManagementState = DEFAULT_STATE,
  action: GenelcoBatchFileAction
): GenelcoBatchFileManagementState => {
  switch (action.type) {
    case GenelcoBatchFileActionType.CHANGE_GENELCO_BATCH_FILES:
      return {
        ...state,
        genelcoBatchFileManagementPage: action.payload,
      };
    case GenelcoBatchFileActionType.CHANGE_CHANGE_GENELCO_BATCH_FILES_FILTER:
      return {
        ...state,
        genelcoBatchFileFilters: action.payload.filters,
      };
    case GenelcoBatchFileActionType.CHANGE_REGENERATE_BATCH_FILE:
      return {
        ...state,
        isBatchRegenerationCompleted: action.payload.isRegenerated,
      };
    case GenelcoBatchFileActionType.CHANGE_REAUDIT_BATCH_FILE:
      return {
        ...state,
        isBatchReAuditCompleted: action.payload.isAuditCompleted,
      };
      case GenelcoBatchFileActionType.CLEAR_GENELCO_BATCH_FILES:
        debugger
        return {
          ...state,
          genelcoBatchFileManagementPage: DEFAULT_STATE.genelcoBatchFileManagementPage,
        };  
        case GenelcoBatchFileActionType.CHANGE_GENELCO_BATCH_FILE_SORT:
      return {
        ...state,
        sort: O.fromNullable(action.payload.sort),
      };  
    default:
      return state;
  }
};